/**
 * rank — single ranking entry point, feature-scoped output.
 *
 * Takes scopes + commands + async-provider results + parsed query, scores
 * everything with the same engine, applies frecency + current-scope
 * overlays, dedupes by id, buckets by FEATURE, caps per feature, and
 * sorts feature groups so the best-scoring feature surfaces at the top.
 *
 * The grouping axis is FEATURE, not zone. The shape of the output is
 * "Mental Math" / "Notes" / "Chat" — same vocabulary as the AppHeader —
 * not "Commands" / "Go to" / "Results". Type-of-result information lives
 * in the row layout (inline crumb chain for places, shortcut hint for
 * actions), not in a separate header.
 */
import type {
  Command,
  FeatureKey,
  FeatureRender,
  Result,
  ScopeNode,
} from "../types";
import { featureFromId, labelFor, priorityOf } from "./features";
import { frecencyBonus } from "./frecency";
import { scoreFields } from "./score";
import { getScopeChain } from "../registry/scopes";

interface RankInput {
  query: string;
  scopes: ScopeNode[];
  commands: Command[];
  asyncResults: Result[];
  currentScopeId: string | null;
}

const PER_FEATURE_CAP = 8;
const MIN_SCORE = 1;
const B_CURRENT_SCOPE = 18;

// Subtitle weight is deliberately small (0.35) so a substring hit in
// subtitle ("…beside your notes") can never outweigh a title hit. Keywords
// stay relevant for conceptual synonyms ("ai" → chat) but never beat title.
function scoreScope(scope: ScopeNode, query: string) {
  return scoreFields(
    [
      { text: scope.title, weight: 1.0, surfaceForHighlight: true },
      { text: scope.shortTitle ?? "", weight: 0.95 },
      { text: scope.subtitle ?? "", weight: 0.35 },
      { text: (scope.keywords ?? []).join(" "), weight: 0.6, surfaceForHighlight: false },
    ],
    query,
  );
}

function scoreCommand(cmd: Command, query: string) {
  return scoreFields(
    [
      { text: cmd.title, weight: 1.0, surfaceForHighlight: true },
      { text: cmd.subtitle ?? "", weight: 0.35 },
      { text: (cmd.keywords ?? []).join(" "), weight: 0.6, surfaceForHighlight: false },
    ],
    query,
  );
}

function scoreResult(r: Result, query: string) {
  return scoreFields(
    [
      { text: r.title, weight: 1.0, surfaceForHighlight: true },
      { text: r.subtitle ?? "", weight: 0.35 },
    ],
    query,
  );
}

const FALLBACK_CTX = {
  router: { push: () => {}, replace: () => {}, back: () => {} },
  pathname: "",
  close: () => {},
  push: () => {},
  pop: () => {},
  reset: () => {},
};

/** Score a flat list of scopes against a query and return Results sorted
 *  by descending score. Used by ScopePage for filtering children inside
 *  a drilled scope (no feature bucketing, no async, no frecency). */
export function rankScopesForPage(
  scopes: ScopeNode[],
  query: string,
  currentScopeId?: string,
): Result[] {
  const out: Result[] = [];
  for (const s of scopes) {
    const m = scoreScope(s, query);
    if (m.score < MIN_SCORE) continue;
    const boost = currentScopeId && s.id.startsWith(currentScopeId) ? B_CURRENT_SCOPE : 0;
    const chain = getScopeChain(s.id);
    out.push({
      id: `scope:${s.id}`,
      title: s.title,
      subtitle: s.subtitle,
      icon: s.icon,
      scopeChain: chain.slice(0, -1),
      feature: s.feature ?? featureFromId(s.id),
      score: m.score + boost,
      positions: m.positions,
      onSelect: (ctx) => {
        if (s.onSelect) return s.onSelect(ctx);
        if (s.href) {
          ctx.router.push(s.href);
          ctx.close();
          return;
        }
        ctx.push({ kind: "scope", scopeId: s.id });
      },
    });
  }
  return out.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}

export function rank(input: RankInput): FeatureRender[] {
  const { query, scopes, commands, asyncResults, currentScopeId } = input;
  const out: Result[] = [];

  // --- Scopes ---
  for (const s of scopes) {
    const m = scoreScope(s, query);
    if (m.score < MIN_SCORE) continue;
    const boost =
      (currentScopeId && s.id.startsWith(currentScopeId) ? B_CURRENT_SCOPE : 0) +
      frecencyBonus(`scope:${s.id}`);
    const chain = getScopeChain(s.id);
    out.push({
      id: `scope:${s.id}`,
      title: s.title,
      subtitle: s.subtitle,
      icon: s.icon,
      scopeChain: chain.slice(0, -1),
      feature: s.feature ?? featureFromId(s.id),
      score: m.score + boost,
      positions: m.positions,
      onSelect: (ctx) => {
        if (s.onSelect) return s.onSelect(ctx);
        if (s.href) {
          ctx.router.push(s.href);
          ctx.close();
          return;
        }
        ctx.push({ kind: "scope", scopeId: s.id });
      },
    });
  }

  // --- Commands ---
  for (const c of commands) {
    if (c.visibleWhen && !c.visibleWhen(FALLBACK_CTX)) continue;
    const m = scoreCommand(c, query);
    if (m.score < MIN_SCORE) continue;
    const boost =
      (currentScopeId && c.scopeId === currentScopeId ? B_CURRENT_SCOPE : 0) +
      frecencyBonus(`cmd:${c.id}`);
    out.push({
      id: `cmd:${c.id}`,
      title: c.title,
      subtitle: c.subtitle,
      icon: c.icon,
      scopeChain: [],
      feature: c.feature ?? featureFromId(c.id),
      score: m.score + boost,
      positions: m.positions,
      shortcut: c.shortcut,
      onSelect: (ctx) => {
        if (c.confirm) ctx.push({ kind: "confirm", command: c });
        else {
          void c.run(ctx);
          ctx.close();
        }
      },
    });
  }

  // --- Async provider results ---
  for (const r of asyncResults) {
    const m = scoreResult(r, query);
    const baseScore = m.score < MIN_SCORE ? (r.score ?? 40) : m.score;
    const boost = frecencyBonus(r.id);
    out.push({
      ...r,
      feature: r.feature ?? featureFromId(r.id),
      score: baseScore + boost,
      positions: m.positions,
    });
  }

  // --- Dedup by id (highest score wins). ---
  const byId = new Map<string, Result>();
  for (const r of out) {
    const prev = byId.get(r.id);
    if (!prev || (r.score ?? 0) > (prev.score ?? 0)) byId.set(r.id, r);
  }

  // --- Bucket by feature. ---
  const byFeature = new Map<FeatureKey, Result[]>();
  for (const r of byId.values()) {
    const f: FeatureKey = r.feature ?? "app";
    const arr = byFeature.get(f) ?? [];
    arr.push(r);
    byFeature.set(f, arr);
  }

  // --- Per-feature sort + cap + group-sort by top score. ---
  const groups = [...byFeature.entries()].map(([feature, results]) => {
    results.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    return {
      feature,
      label: labelFor(feature),
      total: results.length,
      results: results.slice(0, PER_FEATURE_CAP),
    };
  });
  groups.sort((a, b) => {
    const aTop = a.results[0]?.score ?? 0;
    const bTop = b.results[0]?.score ?? 0;
    if (bTop !== aTop) return bTop - aTop;
    return priorityOf(a.feature) - priorityOf(b.feature);
  });
  return groups;
}
